// The story's ONE automation, and the thread suggestions its absence produces (busy-timeline-srv#21).
//
// Until now, connecting an email group to an ongoing meant every future email from that sender was
// linked to it, silently and forever. The AI Brain spec forbids that by default — "sender alone
// never creates a relationship" — so the service now OFFERS those threads instead, and links them
// only where the user has switched auto-connect on for that story.
//
// Two surfaces follow from that, and both of their strings live here for the reason
// lib/storyConnections.js gives: the same sentence appears in the connections panel, the Briefing
// and the review list, and one module is what keeps them from drifting.
//
//   • the switch itself, on the connections panel — ONE per ongoing. The rule is per story, not per
//     address: a story that watches four senders trusts them as a set, and neither the Figma file
//     nor the issue shows a per-address switch. If a per-address rule is ever designed, the panel
//     would list one row per connection and the service would need a key per address — noted rather
//     than guessed at;
//   • the "this thread may relate to <story>" rows in the review list, which are graph EDGES: they
//     are accepted or refused by confirming/rejecting the edge, not by touching the ongoing;
//   • the ACTION rows the suggestion engine adds beside them (busy-timeline-srv#23): "create a task
//     from this thread?", "mark as waiting?", "add to <pack>?" — each with the engine's reason and
//     the signals behind it, answered with Do it / Not now on `suggestions/actions/{id}`.
//
// Pure — no store, no api, nothing mounted.

export const TEXT = {
  // The switch (one per ongoing).
  autoConnectLabel: 'Auto-connect new threads',
  autoConnectOn: 'New emails from these people are added to this Ongoing automatically.',
  autoConnectOff: 'New emails from these people are suggested, so you choose what belongs.',
  autoConnectFailed: 'Could not change that. Try again.',
  // The review list's thread rows.
  threadHeading: 'May relate to your ongoings',
  add: 'Add',
  notRelated: 'Not related',
  viewOngoing: 'View Ongoing',
  // The action rows (busy-timeline-srv#23).
  doIt: 'Do it',
  notNow: 'Not now',
  createTask: 'Create a task from this thread?',
  markWaiting: 'Mark as waiting for a reply?',
  createEvent: 'Add this to your calendar?',
  saveToPack: 'Add to your research?',
  // The reply rows (busy-timeline-srv#34). Not a question — the mail arrived — so the answers are
  // "look at it" and "not now", not "do it" and "don't".
  open: 'Open',
  dismiss: 'Dismiss',
}

/** The kebab-case action kinds timeline-srv speaks. */
export const ACTION_KINDS = {
  createTask: 'create-task',
  markWaiting: 'mark-waiting',
  createEvent: 'create-event',
  saveToPack: 'save-to-pack',
}

/**
 * The fifth kind (busy-timeline-srv#34) — deliberately NOT in ACTION_KINDS above: that map is the
 * four rows answered with "Do it" / "Not now", and a reply review is answered with Open / Dismiss.
 * Keeping it out is what stops it being drawn as a question the user is meant to decide.
 */
export const REVIEW_REPLY = 'review-reply'

/** The automation endpoint's path under `ongoing/{id}` — `GET`/`PUT` the same one. */
export const AUTOMATION_PATH = 'automation'

/** Is auto-connect on? Anything the server has not answered reads as OFF, which is the default. */
export function isAutoConnect(rule) {
  return !!(rule && rule.autoConnectNewThreads)
}

/** The body a `PUT` sends. */
export function automationBody(on) {
  return { autoConnectNewThreads: !!on }
}

/** The line under the switch — it states what IS happening, not what the switch would do. */
export function autoConnectHelp(on) {
  return on ? TEXT.autoConnectOn : TEXT.autoConnectOff
}

// ── The review list ─────────────────────────────────────────────────────────────────────────────
// One list, two kinds of row (`kind` on each item, from timeline-srv): "ongoing" — a whole new story
// is proposed; "thread" — an existing story may want this thread.

/** Is this row an offer to an EXISTING story? */
export function isThreadSuggestion(item) {
  return !!item && item.kind === 'thread' && !!item.relationshipId
}

/**
 * Is this row a POSSIBLE CONNECTION to an existing story — a thread offer OR an entity offer?
 *
 * Pouria, 2026-10-01, frame 40011046:161537: the Ongoing's "Possible connections" table lists every
 * module — ✓ "Buy Lens cleaner", 🌐 "Best sun hat for travel", ✉ "Installment payment", ✎ "Sun
 * essentials list", 📅 "Summer Review" — and since busy-timeline-srv#42 the suggestion list carries
 * the non-email ones as `kind: 'entity'` rows (with a `module`), answered on the SAME
 * `relationships/{id}/confirm|reject` pair a thread offer is. Only the thread half was ever read,
 * so the table showed the one email and nothing else.
 *
 * A separate predicate on purpose: `isThreadSuggestion` still means "an EMAIL may belong to a
 * story", and the surfaces built on that sentence — the Email Workspace's match cards, home's
 * "N emails may belong to an Ongoing" line, the Follow-ups — must not start asking about tasks.
 * Only the story's own connections table and its Briefing row read this one.
 */
export function isConnectionSuggestion(item) {
  return !!item && (item.kind === 'thread' || item.kind === 'entity') && !!item.relationshipId
}

/** Every possible connection — threads and entities — in the order the server sent them. */
export function connectionSuggestions(items) {
  return (items || []).filter(isConnectionSuggestion)
}

/** Is this row something to DO with a thread (busy-timeline-srv#23)? It needs an id to answer on and a kind to name. */
export function isActionSuggestion(item) {
  return !!item && item.kind === 'action' && !!item.id && !!item.actionKind
}

/** The new-story offers, which keep the confirm/dismiss buttons they always had. */
export function ongoingSuggestions(items) {
  return (items || []).filter(i => i && !isThreadSuggestion(i) && !isActionSuggestion(i))
}

/** Where a story is read: the route "View Ongoing" takes. Null without a story. */
export function ongoingRoute(ongoingId, packId = null) {
  const id = String(ongoingId || '').trim()
  if (!id) return null
  const pack = String(packId || '').trim()
  return pack ? `/ongoing/${id}?pack=${pack}` : `/ongoing/${id}`
}

/** The signal names as one readable line ("shared participants · subject match"). Empty for none. */
export function signalsLine(signals) {
  return (Array.isArray(signals) ? signals : [])
    .map(s => String(s || '').trim().replace(/[-_]+/g, ' '))
    .filter(Boolean)
    .join(' · ')
}

/** The thread offers, in the order the server sent them (newest first). */
export function threadSuggestions(items) {
  return (items || []).filter(isThreadSuggestion)
}

/**
 * One thread row as the list draws it: what it is about, which story wants it, and why.
 *
 * The title is the THREAD's subject — the question being asked is "does this email belong there?",
 * so the email has to be what the row names. The story appears under it, in the sentence.
 */
export function threadRow(item) {
  if (!isThreadSuggestion(item)) return null
  const story = String(item.ongoingTitle || item.title || '').trim()
  return {
    id: item.relationshipId,
    relationshipId: item.relationshipId,
    ongoingId: item.ongoingId || null,
    timelineId: item.timelineId || null,
    subject: String(item.subject || '').trim() || 'This email',
    story,
    text: story ? `This thread may relate to ${story}` : 'This thread may relate to one of your ongoings',
    reason: String(item.reason || '').trim(),
    signals: signalsLine(item.signals),
    confidence: typeof item.confidence === 'number' ? item.confidence : null,
    // "View Ongoing" — the third answer (busy-timeline-srv#23): look before deciding.
    viewRoute: ongoingRoute(item.ongoingId),
  }
}

/** Every thread row, junk dropped. */
export function threadRows(items) {
  return threadSuggestions(items).map(threadRow).filter(Boolean)
}

/** The paths the two answers call (`ongoing/relationships/{id}/…` through the BFF). */
export function confirmPath(relationshipId) {
  return `relationships/${String(relationshipId || '')}/confirm`
}

export function rejectPath(relationshipId) {
  return `relationships/${String(relationshipId || '')}/reject`
}

// ── The action rows (busy-timeline-srv#23) ──────────────────────────────────────────────────────

/** The question a row asks, by kind. A pack offer names the pack when the server did. */
export function actionQuestion(item) {
  const kind = String(item?.actionKind || '')
  if (kind === ACTION_KINDS.createTask) return TEXT.createTask
  if (kind === ACTION_KINDS.markWaiting) return TEXT.markWaiting
  if (kind === ACTION_KINDS.createEvent) return TEXT.createEvent
  if (kind === ACTION_KINDS.saveToPack) {
    const pack = String(item?.packTitle || '').trim()
    return pack ? `Add to ${pack}?` : TEXT.saveToPack
  }
  return ''
}

/**
 * One action row as the list draws it: the question, the thread it is about, the why and the
 * signals. Null for anything that is not an action offer, or one of a kind this build cannot ask.
 */
export function actionRow(item) {
  if (!isActionSuggestion(item)) return null
  const text = actionQuestion(item)
  if (!text) return null
  return {
    id: item.id,
    actionKind: item.actionKind,
    timelineId: item.timelineId || null,
    ongoingId: item.ongoingId || null,
    packId: item.packId || null,
    packTitle: String(item.packTitle || '').trim() || null,
    subject: String(item.subject || '').trim() || 'This email',
    text,
    reason: String(item.reason || '').trim(),
    signals: signalsLine(item.signals),
    confidence: typeof item.confidence === 'number' ? item.confidence : null,
    viewRoute: ongoingRoute(item.ongoingId),
  }
}

/** Every action row, junk dropped, in the order the server sent them. Reply reviews are their own
 *  kind of row (see below) and never appear here. */
export function actionRows(items) {
  return (items || []).filter(i => isActionSuggestion(i) && !isReplyReview(i)).map(actionRow).filter(Boolean)
}

/**
 * The action rows of ONE ongoing (busy-my-web#295).
 *
 * The offers used to be asked on home, over every story at once; they are asked inside the story
 * they are about now, so the list has to be narrowed to it. Anything without an `ongoingId` belongs
 * to no story and is therefore in no story's block — it would otherwise appear under every one.
 */
export function actionRowsFor(items, ongoingId) {
  const id = String(ongoingId || '').trim()
  if (!id) return []
  return actionRows(items).filter(row => String(row.ongoingId || '') === id)
}

// ── The reply rows (busy-timeline-srv#34) ───────────────────────────────────────────────────────
// "Supplier replied on Wholesale price list — review response". The server has already collapsed
// them: ONE row per ongoing, with the replies behind it in `replies`. So there is no grouping to do
// here — only the two answers, and where each of them goes.
//
// busy-my-web#276 drew one row per incoming reply and #289 took them all off Home for it. This is
// the shape that was asked for instead: one line per story, however much mail arrived.

/** Is this row a reply waiting to be reviewed? */
export function isReplyReview(item) {
  return !!item && item.kind === 'action' && item.actionKind === REVIEW_REPLY
}

/** Where an email thread is read. Null without one. */
export function threadRoute(timelineId) {
  const id = String(timelineId || '').trim()
  return id ? `/emails/timeline/${id}` : null
}

/**
 * One reply row as Home draws it. Open goes to the THREAD when the row is about a single reply, and
 * to the STORY when it stands for several — there is no one thread to open then, and the story is
 * where its Focus lists them.
 */
export function replyRow(item) {
  if (!isReplyReview(item)) return null
  const replies = Array.isArray(item.replies) ? item.replies.filter(Boolean) : []
  const count = Number(item.count) || replies.length || 1
  const text = String(item.title || '').trim()
  if (!text) return null
  return {
    id: item.id,
    count,
    ongoingId: item.ongoingId || null,
    ongoingTitle: String(item.ongoingTitle || '').trim() || null,
    timelineId: item.timelineId || null,
    subject: String(item.subject || '').trim() || null,
    text,
    replies,
    // The ids Dismiss answers on — every reply the row stands for, so a collapsed row is one click.
    dismissIds: replies.map(r => r && r.suggestionId).filter(Boolean),
    openRoute: (count === 1 && threadRoute(item.timelineId)) || ongoingRoute(item.ongoingId),
  }
}

/** Every reply row, junk dropped. */
export function replyRows(items) {
  return (items || []).filter(isReplyReview).map(replyRow).filter(Boolean)
}

/**
 * The reply rows of ONE ongoing (busy-my-web#295) — the same narrowing `actionRowsFor` does, and for
 * the same reason: the row is answered where the story is. One row per story, so this is at most one.
 */
export function replyRowsFor(items, ongoingId) {
  const id = String(ongoingId || '').trim()
  if (!id) return []
  return replyRows(items).filter(row => String(row.ongoingId || '') === id)
}

/**
 * The individual replies of one story, as its Focus lists them: the collapsed row says how many,
 * the story says which. Newest first, the order the server sent them in.
 */
export function replyFocusItems(items, ongoingId) {
  const id = String(ongoingId || '').trim()
  if (!id) return []
  return replyRows(items)
    .filter(row => String(row.ongoingId || '') === id)
    .flatMap(row => row.replies)
    .map(r => ({
      text: String(r.reason || r.subject || '').trim(),
      timelineId: r.timelineId || null,
      suggestionId: r.suggestionId || null,
    }))
    .filter(item => !!item.text)
}

/** The paths the two answers call, under `suggestions/` through the BFF. */
export function acceptActionPath(id) {
  return `actions/${String(id || '')}/accept`
}

export function dismissActionPath(id) {
  return `actions/${String(id || '')}/dismiss`
}

/**
 * What "Do it" leads to, from the server's answer: which creation mode to open, prefilled — or a
 * route, or nothing more (the server already did it). The SmartBox opens a creation mode on the
 * `smartbox-edit-entity` event with `{ menu, title, dueDate, ongoingId }`, which is the `detail`
 * answered here; a pack save is a place to go, not a form.
 *
 *   { kind: 'entity', detail }   open the task/event form, filled from the thread
 *   { kind: 'route', route }     go to the story (the pack focused when there is one)
 *   { kind: 'done' }             nothing to open — mark-waiting is done server-side
 *   null                         an answer this build cannot act on
 */
export function acceptedOpener(payload) {
  if (!payload) return null
  const mode = String(payload.mode || '')
  const p = payload.prefill || {}
  if (payload.done || mode === 'waiting') return { kind: 'done' }
  if (mode === 'task' || mode === 'event') {
    return {
      kind: 'entity',
      detail: {
        menu: mode === 'task' ? 'tasks' : 'events',
        title: String(p.title || '').trim(),
        dueDate: p.dueDate || '',
        ongoingId: p.ongoingId || null,
        timelineId: p.timelineId || null,
        origin: 'suggestion',
      },
    }
  }
  if (mode === 'pack') {
    const route = ongoingRoute(p.ongoingId, p.packId)
    return route ? { kind: 'route', route } : null
  }
  return null
}

// ── The follow-ups block steps aside while something is open (busy-my-web#402) ──────────────────
//
// Fatemeh, reading an email opened out of a story's "1 possible relation": "The area I circled in
// red is the only space where I can scroll and read the email, and it's very hard because it's tiny
// … when the user opens / edits / selects something, we should temporarily take the follow-ups
// away."
//
// The block is a stack of pending QUESTIONS, and a question is something to come back to — so it is
// collapsed to one line rather than hidden: the count stays visible (nothing silently disappears)
// and pressing the line brings the questions back. The height it gives up goes to whatever the user
// actually opened.

/** The collapsed line's words. Singular at one, as everything else in this file is. */
export function followUpsSummary(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0))
  return `${n} follow-up${n === 1 ? '' : 's'} pending`
}

/**
 * Is the story's middle BUSY — is the user reading, editing or selecting something in it?
 *
 * Every one of these takes the middle column over (an email, a research pack, a comparison, the
 * connections list) or is the user working on one thing in it (a step open in the input box, a step
 * selected on the column). Each is a fact the page already holds; gathering the rule here keeps it
 * out of the template and testable without a page.
 */
export function storyMiddleBusy({
  emailOpen = false, packOpen = false, comparing = false,
  connectionsOpen = false, entityOpen = false, stepSelected = false,
} = {}) {
  return !!(emailOpen || packOpen || comparing || connectionsOpen || entityOpen || stepSelected)
}
